import { useForm } from "react-hook-form";
import { Stack, Typography } from "@mui/material";
import przezmorzeLogo from "../../../images/przezmorze_192x192.png";
import type { FC } from "react";
import FormTextField, { emailValidationPattern } from "../components/formComponents/FormTextField";
import AsyncLoadingButton from "../components/asyncLoadingButton/AsyncLoadingButton";
import { auth, isErrorWithCode } from "../auth/auth";
import { useSnackbar } from "notistack";

interface Props {
  readonly minWidth?: string;
}

interface ResetPasswordFormState {
  readonly email: string;
}

function getResetPasswordErrorDetails(error: unknown): string {
  if (!error) {
    return "nd.";
  }
  if (isErrorWithCode(error) && error.code === "auth/user-not-found") {
    return "Użytkownik z takim adresem email nie istnieje";
  }
  if (typeof error === "object" && "message" in error && typeof error.message === "string") {
    return error.message;
  }
  return JSON.stringify(error);
}

const ResetPassword: FC<Props> = ({ minWidth }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { control, handleSubmit } = useForm<ResetPasswordFormState>({
    defaultValues: { email: "" },
  });

  const onSubmit = async (email: string) => {
    try {
      await auth.sendPasswordResetEmail(email);
      enqueueSnackbar({
        variant: "success",
        message: `Wiadomość email została wysłana. Sprawdź swoją skrzynkę pocztową.`,
      });
    } catch (error) {
      // auth/user-not-found
      const reason = getResetPasswordErrorDetails(error);
      enqueueSnackbar({
        variant: "error",
        message: `Nie udało się wysłać wiadomości email. Powód: ${reason}`,
      });
    }
  };

  return (
    <Stack
      direction="column"
      spacing={5}
      sx={{
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
      }}
    >
      <Stack
        direction="column"
        spacing={2}
        sx={{
          alignItems: "center",
        }}
      >
        <img src={przezmorzeLogo} width={192} alt="Logo" />
        <Typography variant="subtitle1">Odzyskaj dostęp do konta</Typography>
      </Stack>
      <Stack
        direction="column"
        spacing={2}
        sx={{
          alignItems: "center",
          minWidth: minWidth,
        }}
      >
        <FormTextField
          type="email"
          name="email"
          label="Email"
          control={control}
          rules={{ pattern: emailValidationPattern }}
        />
        <AsyncLoadingButton onClick={handleSubmit((s) => onSubmit(s.email))}>Wyślij email</AsyncLoadingButton>
      </Stack>
    </Stack>
  );
};

export default ResetPassword;
